// Pure byte encoders for the mobile terminal key toolbar. Phone keyboards lack
// Tab / Esc / Ctrl / arrow keys, so the toolbar synthesizes the exact bytes a
// PTY expects and feeds them through TerminalSocket.sendInput. These functions
// are intentionally side-effect free so they can be unit-tested directly.

export type ArrowDir = 'up' | 'down' | 'right' | 'left';

// ARROW maps a direction to its ANSI CSI escape sequence. These are the same
// sequences xterm itself emits for the arrow keys in normal cursor mode.
export const ARROW: Record<ArrowDir, string> = {
  up: '\x1b[A',
  down: '\x1b[B',
  right: '\x1b[C',
  left: '\x1b[D',
};

// esc() returns the single Escape byte (0x1b).
export function esc(): Uint8Array {
  return new Uint8Array([0x1b]);
}

// tab() returns the single Tab byte (0x09) — this is what unlocks shell
// tab-completion from a phone keyboard.
export function tab(): Uint8Array {
  return new Uint8Array([0x09]);
}

// arrow() returns the byte triplet for a cursor-key escape sequence.
export function arrow(dir: ArrowDir): Uint8Array {
  const seq = ARROW[dir];
  const out = new Uint8Array(seq.length);
  for (let i = 0; i < seq.length; i++) out[i] = seq.charCodeAt(i) & 0xff;
  return out;
}

// ctrlByte() maps a printable character to its control-code equivalent by
// masking the low 5 bits, e.g. 'c' -> 3 (Ctrl-C), 'a' -> 1 (Ctrl-A). It is
// case-insensitive so a capitalized letter produces the same control code.
export function ctrlByte(ch: string): number {
  return ch.toLowerCase().charCodeAt(0) & 0x1f;
}
